import { useMe } from '@/api/user/use-me';
import PageLoader from '@/components/ui/Loader/PageLoader';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/authStore';
import { checkRoutePermission } from '@/utils/permissions';
import { notifications } from '@mantine/notifications';
import { ShieldX } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Layout from './Layout';

interface AuthGuardProps {
  requireAuth?: boolean;
}

const AuthGuard = ({ requireAuth = false }: AuthGuardProps) => {
  // we need the user query call to populate the auth store
  useMe();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const userPermissions = useAuthStore((state) => state.user?.groups);

  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  if (!requireAuth && isAuthenticated)
    return <Navigate to={ROUTES.DASHBOARD} replace />;

  if (requireAuth && !isAuthenticated)
    return <Navigate to={ROUTES.LOGIN} replace />;

  // Permission check based on pathname
  const hasPermission = checkRoutePermission(
    userPermissions,
    location.pathname
  );

  if (!hasPermission) {
    notifications.show({
      title: 'Permission Denied',
      message: 'You do not have permission to access this page.',
      color: 'red',
      icon: <ShieldX />,
    });
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

export default AuthGuard;
